import { useState } from "react";
import { TypedFilter } from "odata-query";
import { subscriptionService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { IApiRequest, IDepartmentResponse } from "@miplatform/types";
import { createUUID } from "@miplatform/utils";

const getDepartmentsUUID = createUUID();
const getRecentDepartmentsUUID = createUUID();

export const useDepartments = ({
  debouncedSearch = "",
}: IUseDepartmentsProps = {}) => {
  const stateManager = useComponentState();

  const [departments, setDepartments] = useState<IDepartmentResponse[]>([]);
  const [recentDepartments, setRecentDepartments] = useState<
    IDepartmentResponse[]
  >([]);
  const [filter, setFilter] = useState<TypedFilter<IDepartmentResponse>>({});

  const getDepartments = async ({
    top,
    skip,
    sort,
    filters = {},
    params,
    signal,
  }: IApiRequest = {}) => {
    stateManager.abort(getDepartmentsUUID);

    const searchFilter = debouncedSearch
      ? { name: { contains: debouncedSearch } }
      : {};

    const combinedFilters = {
      ...filter,
      ...filters,
      ...searchFilter,
    };

    const response = await stateManager.run(async () => {
      return subscriptionService.departments.getAll({
        sort: sort ?? "name",
        top,
        skip,
        filters: combinedFilters,
        ...params,
        signal,
      });
    }, getDepartmentsUUID);
    if (response.status === HTTP_STATUSES.OK) {
      setDepartments(response.data);
      return response.data;
    }
    return null;
  };

  const getRecentDepartments = ({ sort, filters, top }: IApiRequest = {}) => {
    const combinedFilters = {
      ...filter,
      ...filters,
      name: { contains: debouncedSearch },
    };

    return stateManager.run(async () => {
      return subscriptionService.departments.getAll({
        sort,
        filters: combinedFilters,
        top,
      });
    }, getRecentDepartmentsUUID);
  };

  return {
    departments,
    loading: stateManager.isBusy(),
    getDepartments,
    setDepartments,
    recentDepartments,
    setRecentDepartments,
    getRecentDepartments,
    filter,
    setFilter,
  };
};

interface IUseDepartmentsProps {
  debouncedSearch?: string;
}
